import React, { useEffect, useMemo } from 'react'
import { Box, styled, useMediaQuery, useTheme } from '@mui/material'
import { CalculatorContainer } from './CalculatorContainer'
import DraggableDialog from '@app/components/ui/modals/DraggableDialog'
import { useSnapshot } from 'valtio'
import { CurrentTestState, CurrentUserAssessmentState } from '@app/storage'
import { resetCalculatorData } from '@app/helpers'
import { CalculatorVersion } from '@app/types'
import { TICalculator } from './TICalculator'

type Props = {
  open: boolean
  onClose: () => void
  version?: CalculatorVersion
}

const DRAGGABLE_CALCULATOR_ID = 'DRAGGABLE_CALCULATOR_ID'

export const CalculatorDraggableModal: React.FC<Props> = ({ open, onClose, version = 'TI108' }) => {
  const { currentItemResponse } = useSnapshot(CurrentTestState)
  const { program } = useSnapshot(CurrentUserAssessmentState)

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('mobile'))

  const calculatorVersion: CalculatorVersion = useMemo(
    () => (program === 'STAAR' ? 'DESMOS' : version),
    [program, version],
  )

  const isTICalculator = useMemo(() => calculatorVersion !== 'DESMOS', [calculatorVersion])

  const paperStyles = useMemo(
    () =>
      isTICalculator
        ? { maxWidth: 'fit-content', minWidth: '200px' }
        : {
            width: isMobile ? '80%' : '60%',
            height: '60%',
            maxWidth: isMobile ? '350px' : '600px',
            maxHeight: isMobile ? '350px' : '600px',
          },
    [isTICalculator, isMobile],
  )

  useEffect(() => {
    resetCalculatorData()
  }, [currentItemResponse.itemId])

  return (
    <DraggableDialog
      id={DRAGGABLE_CALCULATOR_ID}
      open={open}
      title='Calculator'
      paperStyles={paperStyles}
      onClose={onClose}
      disableBackdropClose
      customDefaultCoordinates={{ y: 82, x: 24 }}
    >
      <CalculatorWrapper>
        {isTICalculator ? (
          <TICalculator version={calculatorVersion} />
        ) : (
          <CalculatorContainer itemId={currentItemResponse.itemId} />
        )}
      </CalculatorWrapper>
    </DraggableDialog>
  )
}

const CalculatorWrapper = styled(Box)(() => ({
  width: '100%',
  height: '100%',
}))
